import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  ButtonGroup,
  Slider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import TextHighlight from './TextHighlight';
import Navigation from './Navigation';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';

interface User {
  id: string;
  username: string;
}

interface Classification {
  value: string;
  label: string;
}

interface Message {
  id: number;
  channel: string;
  user: string;
  text: string;
  timestamp: string;
  classification: string | null;
}

interface MessageGroup {
  id: string;
  channel: string;
  messages: Message[];
  matching_message_ids: number[];
  timestamp: string;
}

interface Filters {
  channel: string;
  user: string;
  keyword: string;
  startDate: Date | null;
  endDate: Date | null;
  classification: string;
}

const MessagesView: React.FC = () => {
  const [groups, setGroups] = useState<MessageGroup[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [classifications, setClassifications] = useState<Classification[]>([]);
  const [filters, setFilters] = useState<Filters>({
    channel: '',
    user: '',
    keyword: '',
    startDate: null,
    endDate: null,
    classification: '',
  });
  const [contextSize, setContextSize] = useState<number>(5);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [openFiltersModal, setOpenFiltersModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchChannels();
    fetchUsers();
    fetchClassifications();
  }, []);

  useEffect(() => { fetchMessages(page); }, [page]); // Fetch messages when page changes

  const fetchClassifications = async () => {
    try {
      const response = await fetch('/api/classifications', {
        credentials: 'include',
      });
      const data = await response.json();
      setClassifications(data.classifications);
    } catch (error) {
      console.error('Error fetching classifications:', error);
    }
  };

  const fetchChannels = async () => {
    try {
      const response = await fetch('/api/channels', {
        credentials: 'include',
      });
      const data = await response.json();
      setChannels(data.channels);
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        credentials: 'include',
      });
      const data = await response.json();
      setUsers(data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchMessages = async (currentPage: number) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '50',
        context_size: contextSize.toString(),
        ...(filters.channel && { channel: filters.channel }),
        ...(filters.user && { user: filters.user }),
        ...(filters.keyword && { keyword: filters.keyword }),
        ...(filters.startDate && { start_date: filters.startDate.toISOString() }),
        ...(filters.endDate && { end_date: filters.endDate.toISOString() }),
        ...(filters.classification && { classification: filters.classification }),
      });

      const response = await fetch(
        `/api/messages?${queryParams}`,
        {
          credentials: 'include',
        }
      );
      const data = await response.json();
      setGroups(data.groups);
      setTotalPages(data.pages);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClassification = async (messageId: number, classification: string) => {
    try {
      await fetch(`/api/messages/${messageId}/classify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ classification }),
      });
      
      // Update the message in the local state
      setGroups(groups.map(group => ({
        ...group,
        messages: group.messages.map(msg =>
          msg.id === messageId ? { ...msg, classification } : msg
        )
      })));
    } catch (error) {
      console.error('Error classifying message:', error);
    }
  };

  const getClassificationColor = (classification: string | null) => {
    switch (classification) {
      case 'produce':
        return 'success';
      case 'do_not_produce':
        return 'error';
      case 'attorney_client_privilege':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleDownload = async () => {
    try {
      const queryParams = new URLSearchParams({
        context_size: contextSize.toString(),
        ...(filters.channel && { channel: filters.channel }),
        ...(filters.user && { user: filters.user }),
        ...(filters.keyword && { keyword: filters.keyword }),
        ...(filters.startDate && { start_date: filters.startDate.toISOString() }),
        ...(filters.endDate && { end_date: filters.endDate.toISOString() }),
        ...(filters.classification && { classification: filters.classification }),
      });

      const response = await fetch(
        `/api/messages/download?${queryParams}`,
        {
          credentials: 'include',
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to download messages');
      }

      const data = await response.json();
      
      // Create and download the file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Generate filename with current date
      const date = new Date().toISOString().split('T')[0];
      a.download = `slack-messages-${date}.json`;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading messages:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Navigation */}
      <Navigation />
      
      {/* Main Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={6}>
            <Typography variant="h4" color="primary">
              Slack Message Viewer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use filters to search and review messages with context
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setOpenFiltersModal(true)}
              fullWidth
              sx={{ height: '56px' }}
            >
              Search & Filters
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
              fullWidth
              sx={{ height: '56px' }}
              disabled={!filters.keyword}
            >
              Export Results
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Filters Modal */}
      <Dialog 
        open={openFiltersModal} 
        onClose={() => setOpenFiltersModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Search & Filters</Typography>
          <IconButton 
            onClick={() => setOpenFiltersModal(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords (one per line)"
                value={filters.keyword}
                onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
                multiline
                rows={4}
                required
                helperText="Enter search keywords, one per line. Messages matching any keyword will be included."
                placeholder="Enter keywords...&#10;e.g., turnkey&#10;quarterly report&#10;budget planning"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ px: 1 }}>
                <Typography variant="body2" gutterBottom>
                  Context: {contextSize} messages before/after each match
                </Typography>
                <Slider
                  value={contextSize}
                  onChange={(_, value) => setContextSize(value as number)}
                  min={1}
                  max={20}
                  step={1}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' }
                  ]}
                  valueLabelDisplay="auto"
                  size="small"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* Empty grid item for layout */}
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Additional Filters
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Channel</InputLabel>
                <Select
                  value={filters.channel}
                  label="Channel"
                  onChange={(e) => setFilters({ ...filters, channel: e.target.value })}
                >
                  <MenuItem value="">All Channels</MenuItem>
                  {channels.map((channel) => (
                    <MenuItem key={channel} value={channel}>
                      #{channel}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>User</InputLabel>
                <Select
                  value={filters.user}
                  label="User"
                  onChange={(e) => setFilters({ ...filters, user: e.target.value })}
                >
                  <MenuItem value="">All Users</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.username}>
                      {user.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Classification</InputLabel>
                <Select
                  value={filters.classification}
                  label="Classification"
                  onChange={(e) => setFilters({ ...filters, classification: e.target.value })}
                >
                  <MenuItem value="">All Classifications</MenuItem>
                  {classifications.map((classification) => (
                    <MenuItem key={classification.value} value={classification.value}>
                      {classification.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* Empty grid item for layout */}
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Date Range
              </Typography>
            </Grid>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate}
                  onChange={(date) => setFilters({ ...filters, startDate: date })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="End Date"
                  value={filters.endDate}
                  onChange={(date) => setFilters({ ...filters, endDate: date })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </LocalizationProvider>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => {
              setFilters({
                channel: '',
                user: '',
                keyword: '', // Also clear keywords now
                startDate: null,
                endDate: null,
                classification: '',
              });
              setContextSize(5); // Reset context size
            }}
            color="secondary"
          >
            Clear All
          </Button>
          <Button
            onClick={() => {
              setOpenFiltersModal(false);
              setPage(1);
              fetchMessages(1);
            }}
            variant="contained"
            disabled={!filters.keyword.trim() || loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </DialogActions>
      </Dialog>

      {!filters.keyword && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Click "Search & Filters" to enter keywords and search messages with context.
        </Alert>
      )}

      {loading && (
        <Paper sx={{ p: 4, mb: 2, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Searching messages...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This may take a moment for large result sets
          </Typography>
        </Paper>
      )}

      {!loading && groups.map((group) => (
        <Paper key={group.id} sx={{ p: 2, mb: 3, backgroundColor: '#f9f9f9' }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
              Channel: #{group.channel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Context Group ({group.messages.length} messages)
            </Typography>
          </Box>
          {group.messages.map((message) => {
            const isMatching = group.matching_message_ids.includes(message.id);
            return (
              <Paper 
                key={message.id} 
                sx={{ 
                  p: 2, 
                  mb: 1.5, 
                  backgroundColor: 'white',
                  border: isMatching ? '2px solid' : '1px solid #e0e0e0',
                  borderColor: isMatching ? 'primary.main' : '#e0e0e0',
                  boxShadow: isMatching ? '0 0 8px rgba(46, 125, 50, 0.3)' : 'none'
                }}
              >
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    <strong>{message.user}</strong> • {new Date(message.timestamp).toLocaleString()}
                    {isMatching && (
                      <Typography component="span" variant="body2" color="primary" sx={{ ml: 1, fontWeight: 'bold' }}>
                        • MATCH
                      </Typography>
                    )}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <TextHighlight text={message.text} highlight={filters.keyword} />
                </Typography>
                <ButtonGroup variant="contained" size="small">
                  <Button
                    color={message.classification === 'produce' ? 'success' : 'inherit'}
                    onClick={() => handleClassification(message.id, 'produce')}
                  >
                    Produce
                  </Button>
                  <Button
                    color={message.classification === 'do_not_produce' ? 'error' : 'inherit'}
                    onClick={() => handleClassification(message.id, 'do_not_produce')}
                  >
                    Do Not Produce
                  </Button>
                  <Button
                    color={message.classification === 'attorney_client_privilege' ? 'warning' : 'inherit'}
                    onClick={() => handleClassification(message.id, 'attorney_client_privilege')}
                  >
                    Attorney-Client Privilege
                  </Button>
                </ButtonGroup>
              </Paper>
            );
          })}
        </Paper>
      ))}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default MessagesView; 